import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import { useTranslation } from 'react-i18next';
import { firstLine } from '../components/OriginalInputCollapse';
import { confidenceBand } from '../components/report/ReportSections';

export default function RecordsPage() {
  const { t, i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/api/v1/reports?page=${page}&per_page=20`);
      if (res.ok) {
        const data = await res.json();
        setReports(data.reports || data.items || []);
        setTotal(data.total || 0);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [page]);

  useEffect(() => { loadReports(); }, [loadReports]);

  const riskColor = (level) => {
    const map = { LOW: '#16A34A', MEDIUM: '#D97706', HIGH: '#B91C1C', CRITICAL: '#B91C1C' };
    return map[level] || 'var(--text-tertiary)';
  };

  const classColor = (cls) => {
    if (!cls) return 'var(--text-tertiary)';
    const l = cls.toLowerCase();
    // NOT_APPLICABLE (no cosmetics/drug/OTC claim) and ENGLISH_ONLY are benign
    // disclaimer terminals, not a "safe cosmetic" verdict — render neutral grey,
    // never green (which would misleadingly imply a passed compliance check).
    if (l === 'not_applicable' || l === 'english_only') return 'var(--text-tertiary)';
    if (l.includes('drug') && !l.includes('otc')) return '#B91C1C';
    if (l.includes('otc')) return '#D97706';
    return '#16A34A';
  };

  // Friendly label for benign disclaimer classifications; other values render
  // as their raw canonical enum (existing behavior).
  const classLabel = (cls) => {
    if (!cls) return '—';
    if (cls.toUpperCase() === 'NOT_APPLICABLE') return isKo ? '해당 없음' : 'Not Applicable';
    return cls;
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto" style={{ color: 'var(--text-primary)' }}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-headline font-extrabold text-2xl" style={{ color: 'var(--text-primary)' }}>
          {isKo ? '검증 기록' : 'Verification Records'}
        </h1>
        <span className="text-sm" style={{ color: 'var(--text-tertiary)' }}>{total} {isKo ? '건' : 'records'}</span>
      </div>

      {loading ? (
        <div className="text-center py-16" style={{ color: 'var(--text-tertiary)' }}>Loading…</div>
      ) : reports.length === 0 ? (
        <div className="text-center py-16" style={{ color: 'var(--text-tertiary)' }}>
          <span className="material-symbols-outlined block text-5xl mb-3" style={{ color: '#30363D' }}>description</span>
          <p>{isKo ? '아직 검증 기록이 없습니다' : 'No verification records yet'}</p>
          <button onClick={() => navigate('/app')} className="vp-btn-primary text-sm px-5 py-2 mt-4">
            {isKo ? '첫 검증 시작하기' : 'Start your first verification'}
          </button>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {reports.map((r, i) => (
              <button
                key={r.id || i}
                onClick={() => navigate('/reports')}
                className="w-full text-left transition-colors"
                style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: 12, padding: '16px 20px', display: 'block' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#a100aa'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#30363D'}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* §8 P5 T5.4 — firstLine() truncates to a single ~96-char
                        line BEFORE it reaches the DOM, so a page-sized paste
                        doesn't sit in memory just to be CSS-ellipsized. */}
                    <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                      {firstLine(r.input_text || r.text || '—')}
                    </p>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                      {r.source || '—'} · {new Date(r.created_at || r.date).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs font-bold px-2 py-0.5 rounded" style={{ backgroundColor: `${classColor(r.classification)}20`, color: classColor(r.classification) }}>
                      {classLabel(r.classification)}
                    </span>
                    <span className="text-xs font-bold px-2 py-0.5 rounded" style={{ backgroundColor: `${riskColor(r.risk_level)}20`, color: riskColor(r.risk_level) }}>
                      {r.risk_level || '—'}
                    </span>
                    {/* T5.1 — same three-band display as the report itself, not a
                        raw percentage (this list reads the same possibly-floored
                        veto confidence). Raw value stays in `title` for support. */}
                    {typeof r.confidence === 'number' && (
                      <span
                        className="text-xs"
                        style={{ color: 'var(--text-tertiary)' }}
                        title={`${(r.confidence * 100).toFixed(0)}%`}
                      >
                        {t(`report.confidenceBand.${confidenceBand(r.confidence)}`)}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Pagination */}
          {total > 20 && (
            <div className="flex items-center justify-center gap-3 mt-6">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="text-sm px-3 py-1 rounded" style={{ backgroundColor: page > 1 ? 'var(--bg-secondary)' : 'transparent', color: page > 1 ? 'var(--text-primary)' : '#30363D', border: '1px solid var(--border-color)' }}>
                ← {isKo ? '이전' : 'Prev'}
              </button>
              <span className="text-sm" style={{ color: 'var(--text-tertiary)' }}>{page} / {Math.ceil(total / 20)}</span>
              <button onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(total / 20)} className="text-sm px-3 py-1 rounded" style={{ backgroundColor: page < Math.ceil(total / 20) ? 'var(--bg-secondary)' : 'transparent', color: page < Math.ceil(total / 20) ? 'var(--text-primary)' : '#30363D', border: '1px solid var(--border-color)' }}>
                {isKo ? '다음' : 'Next'} →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
